import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Email, ArrowBack, CheckCircle } from '@mui/icons-material';
import { authAPI, ForgotPasswordData } from '../services/api';
import { validateEmail } from '../utils/validation';

const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const [formData, setFormData] = useState<ForgotPasswordData>({
    email: '',
  });

  const [errors, setErrors] = useState<Partial<ForgotPasswordData>>({});

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setFormData(prev => ({
      ...prev,
      email: value,
    }));

    if (errors.email) {
      setErrors(prev => ({
        ...prev,
        email: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ForgotPasswordData> = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.forgotPassword(formData);
      
      if (response.success) {
        setEmailSent(true);
        setSnackbar({
          open: true,
          message: response.message || 'Correo de recuperación enviado exitosamente.',
          severity: 'success',
        });
      }

    } catch (error: any) {
      let errorMessage = 'Error al enviar correo de recuperación';
      
      if (error.message) {
        if (error.message.includes('email') || error.message.includes('correo')) {
          errorMessage = 'El correo electrónico no está registrado';
        } else {
          errorMessage = error.message;
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (emailSent) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            maxWidth: 450,
            width: '100%',
            backgroundColor: '#f8f9fa',
            border: '2px solid #d0d7de',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            textAlign: 'center',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <CheckCircle
              sx={{
                fontSize: 80,
                color: '#10b981',
                mb: 2,
              }}
            />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#333',
              mb: 2,
            }}
          >
            Correo Enviado
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: '#4a5568',
              fontSize: '1.1rem',
              lineHeight: 1.6,
            }}
          >
            Te hemos enviado un enlace para restablecer tu contraseña.
          </Typography>
          
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 4,
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            Revisa tu bandeja de entrada y también la carpeta de spam. El enlace expirará en 24 horas.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setEmailSent(false)}
              sx={{
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                borderColor: '#dee2e6',
                color: '#666',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#adb5bd',
                  backgroundColor: '#f8f9fa',
                },
              }}
            >
              Enviar otro correo
            </Button>
            
            <Link to="/register" style={{ textDecoration: 'none' }}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    backgroundColor: '#333333',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Volver al Registro
              </Button>
            </Link>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 3, // Reducido de 4 a 3
            borderRadius: 3,
            maxWidth: 400, // Reducido de 450 a 400
            width: '100%',
            backgroundColor: '#f8f9fa',
            border: '2px solid #d0d7de',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}> {/* Reducido de mb: 4 a mb: 3 */}
            <Typography
              variant="h5" // Reducido de h4 a h5
              component="h1"
              sx={{
                fontWeight: 700,
                color: '#333',
                mb: 1,
              }}
            >
              ¿Olvidaste tu contraseña?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '0.9rem', // Reducido de 0.95rem a 0.9rem
                lineHeight: 1.6,
                maxWidth: 300, // Reducido de 350 a 300
                mx: 'auto',
              }}
            >
              No te preocupes, ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Correo Electrónico"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
              autoFocus
              sx={{
                mb: 3, // Reducido de mb: 4 a mb: 3
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2, // Reducido de 3 a 2
                mb: 2,
                py: 1.2, // Reducido de 1.5 a 1.2
                fontSize: '1rem', // Reducido de 1.1rem a 1rem
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                backgroundColor: '#000000',
                color: '#ffffff',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#333333',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  backgroundColor: '#666666',
                  color: '#cccccc',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                'Enviar Correo de Recuperación'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Link
                to="/register"
                style={{
                  textDecoration: 'none',
                  color: '#666',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <ArrowBack sx={{ fontSize: 16 }} />
                ¿Recordaste tu contraseña? Volver al Registro
              </Link>
            </Box>

            {/* NUEVO: Link al sistema CONTAX */}
            <Box sx={{ textAlign: 'center', mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ¿Ya tienes cuenta?
              </Typography>
              <Button
                component="a"
                href="https://contax.sigex.pe/auth/jwt/login?returnTo=https%3A%2F%2Fcontax.sigex.pe%2Fdashboard"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                sx={{
                  color: '#000000',
                  borderColor: '#000000',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: '#000000',
                    color: '#ffffff',
                  },
                }}
              >
                Acceder al Sistema CONTAX
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ForgotPassword;